import React from "react";
import { Link } from "react-router-dom";
import MarketingLayout from "../components/MarketingLayout";
import { ArrowUpRight, TrendingUp, Users, Globe, Award, Quote, Heart, Brain, Sparkles, Plus } from "lucide-react";

const STATS = [
    {
        value: "+13%",
        source: "University of Oxford — Saïd Business School (2019)",
        text: "Colaboradores felizes são 13% mais produtivos. Fazem mais vendas, convertem mais chamadas e faltam menos ao trabalho.",
        link: "https://www.ox.ac.uk/news/2019-10-24-happy-workers-are-13-more-productive",
    },
    {
        value: "+27%",
        source: "Gallup — Global Study (1,8M colaboradores, 73 países)",
        text: "Empresas com elevado nível de bem-estar dos colaboradores têm 27% maior rentabilidade.",
        link: "https://www.gallup.com/workplace/215924/well-being.aspx",
    },
    {
        value: "+12%",
        source: "University of Warwick — Economics Research",
        text: "Cerca de 12% de aumento de produtividade quando os colaboradores são mais felizes. O inverso também se verifica: menos felicidade, menos produtividade.",
        link: "https://warwick.ac.uk/newsandevents/pressreleases/new_study_shows/",
    },
    {
        value: "€8,8T",
        source: "Gallup — State of the Global Workplace",
        text: "O custo económico global do mal-estar no trabalho — €8,8 biliões por ano em produtividade perdida.",
        link: "https://www.gallup.com/workplace/",
    },
    {
        value: "+6%",
        source: "Harvard Business Review (2025)",
        text: "As empresas S&P 500 com maior felicidade dos colaboradores ultrapassaram o mercado em ~6% de valorização bolsista ao longo de 11 anos.",
        link: "https://hbr.org/2025/11/why-making-your-employees-happier-pays-off",
    },
    {
        value: "61%",
        source: "Gallup",
        text: "Colaboradores com baixo bem-estar têm 61% mais probabilidade de burnout e 66% mais probabilidade de preocupação diária.",
        link: "https://www.gallup.com/workplace/215924/well-being.aspx",
    },
];

const CLIENTS = ["Porsche", "Sonae", "Inditex", "Millennium", "Bosch", "Capgemini", "Mahle", "ANF", "Alliance Healthcare", "Re/Max", "Grenke", "Opticalia"];

const PRODUCTS = [
    {
        title: "Happiness Architect",
        subtitle: "Serviço de Consultoria IHTP",
        description: "Desenhamos a arquitetura de felicidade da sua organização. Um programa de consultoria estratégica para estruturar cultura, liderança e experiência do colaborador com impacto mensurável.",
        cta: "Adquirir na loja IHTP",
        url: "https://ihavethepower.net/loja/coaching/happiness-architect/",
        image: "https://images.pexels.com/photos/7792803/pexels-photo-7792803.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        tag: "Serviço corporativo",
    },
    {
        title: "Certificação Happiness Manager",
        subtitle: "Formação certificada IHTP",
        description: "Torne-se um Happiness Manager certificado. Formação reconhecida pela IHTP com mais de 20 anos de experiência em gestão de pessoas e transformação organizacional.",
        cta: "Inscrever na certificação",
        url: "https://ihavethepower.net/happiness-manager/",
        image: "https://images.unsplash.com/photo-1706074740295-d7a79c079562?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwyfHxlbGVnYW50JTIwb2ZmaWNlJTIwcHJlc2VudGF0aW9ufGVufDB8fHx8MTc3NzAzMDg4M3ww&ixlib=rb-4.1.0&q=85",
        tag: "Certificação",
    },
];

export default function LandingPage() {
    return (
        <MarketingLayout>
            {/* HERO */}
            <section className="hero-bg grain" data-testid="hero-section">
                <div className="max-w-7xl mx-auto px-6 md:px-10 pt-20 pb-24 grid lg:grid-cols-12 gap-12 items-end">
                    <div className="lg:col-span-7 fade-up">
                        <div className="label-caps mb-8">I Have The Power — há mais de 20 anos a inspirar pessoas em mais de 80 países</div>
                        <h1 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[1.05] text-[#0d1628] max-w-3xl">
                            A felicidade dos seus colaboradores é a métrica mais <span className="italic">subestimada</span> do seu negócio.
                        </h1>
                        <p className="text-[#52525b] text-lg mt-8 max-w-xl leading-relaxed">
                            Estudos da Oxford, Harvard e Gallup provam: empresas com colaboradores felizes são 13% mais produtivas e 27% mais rentáveis. A IHTP ajuda-o a construir essa vantagem competitiva.
                        </p>
                    </div>
                    <div className="lg:col-span-5 border-l border-[#e4e4e7] pl-8 fade-up">
                        <Quote size={28} className="text-[#b89b6f] mb-4" />
                        <blockquote className="font-display italic text-2xl text-[#0d1628] leading-snug">
                            Posso ainda não ter chegado onde queria, mas estou mais perto que ontem.
                        </blockquote>
                        <div className="label-caps mt-6">IHTP — I Have The Power</div>
                    </div>
                </div>
            </section>

            {/* STATISTICS — THE CASE FOR HAPPINESS */}
            <section className="bg-[#0d1628] text-white" data-testid="statistics-section">
                <div className="max-w-7xl mx-auto px-6 md:px-10 py-24">
                    <div className="grid lg:grid-cols-12 gap-12">
                        <div className="lg:col-span-5">
                            <div className="label-caps text-white/60">A ciência confirma</div>
                            <h2 className="font-display text-4xl md:text-5xl mt-6 leading-tight">
                                Não é um <span className="italic">soft topic</span>. É um indicador duro de performance.
                            </h2>
                            <p className="text-white/70 mt-6 leading-relaxed">
                                As universidades e instituições mais conceituadas do mundo medem há décadas o impacto da felicidade nas organizações. Os números são inequívocos.
                            </p>
                        </div>
                        <div className="lg:col-span-7 grid sm:grid-cols-2 gap-px bg-white/10">
                            {STATS.map((s, idx) => (
                                <a key={`stat-${s.value}-${idx}`} href={s.link} target="_blank" rel="noreferrer" className="bg-[#0d1628] p-8 hover:bg-[#1a2c4e] transition-colors flex flex-col group" data-testid={`stat-${idx}`}>
                                    <div className="font-display text-6xl leading-none text-white">{s.value}</div>
                                    <p className="text-sm text-white/95 mt-6 leading-relaxed flex-1">{s.text}</p>
                                    <div className="mt-6 pt-4 border-t border-white/30 flex items-start justify-between gap-3">
                                        <span className="text-xs leading-snug text-white font-medium">
                                            <span className="text-white/60 mr-1.5 uppercase tracking-wider text-[10px]">Fonte</span>
                                            {s.source}
                                        </span>
                                        <ArrowUpRight size={14} className="text-white/80 opacity-70 group-hover:opacity-100 transition-opacity shrink-0 mt-0.5" />
                                    </div>
                                </a>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ABOUT IHTP */}
            <section className="border-t border-[#e4e4e7]" data-testid="about-ihtp-section">
                <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 grid lg:grid-cols-12 gap-12 items-start">
                    <div className="lg:col-span-6">
                        <div className="label-caps">Sobre a IHTP e o fundador</div>
                        <h2 className="font-display text-4xl md:text-5xl text-[#0d1628] mt-6 leading-tight">
                            Reconhecidos internacionalmente em rankings de liderança e <span className="italic">felicidade</span>.
                        </h2>
                        <p className="text-[#52525b] mt-6 leading-relaxed">
                            Fundada pelo <a href="https://ihavethepower.net/fundador/" target="_blank" rel="noreferrer" className="text-[#0d1628] link-underline font-medium" data-testid="founder-link">Dr. Adelino Cunha</a>, a I Have The Power é uma consultora especializada em pessoas e organizações. Há mais de duas décadas desenhamos culturas de excelência em empresas de referência mundial.
                        </p>
                        <div className="grid grid-cols-3 gap-4 mt-10">
                            <div className="border-t border-[#0d1628] pt-4">
                                <div className="font-display text-4xl text-[#0d1628]">20+</div>
                                <div className="label-caps mt-2">Anos</div>
                            </div>
                            <div className="border-t border-[#0d1628] pt-4">
                                <div className="font-display text-4xl text-[#0d1628]">80+</div>
                                <div className="label-caps mt-2">Países</div>
                            </div>
                            <div className="border-t border-[#0d1628] pt-4">
                                <div className="font-display text-4xl text-[#0d1628]">100k+</div>
                                <div className="label-caps mt-2">Vidas tocadas</div>
                            </div>
                        </div>
                        <a href="https://ihavethepower.net/fundador/" target="_blank" rel="noreferrer" className="label-caps mt-10 inline-flex items-center gap-2 link-underline text-[#0d1628]" data-testid="about-learn-more">
                            Saber mais em ihavethepower.net <ArrowUpRight size={12} />
                        </a>
                    </div>

                    <div className="lg:col-span-6 space-y-6">
                        <div className="card-flat border-l-4 border-l-[#b89b6f]" data-testid="founder-accolades-card">
                            <div className="label-caps">Dr. Adelino Cunha — Prémios</div>
                            <ul className="mt-5 space-y-3 text-sm text-[#0d1628]">
                                <li><strong>Happy Boss</strong> — prémio também atribuído ao Presidente da República de Portugal e ao Comendador Nabeiro (Delta Cafés).</li>
                                <li>Primeiro português entre os <strong>TOP 100 Global Coaching Leaders</strong>, numa eleição envolvendo 140 países.</li>
                                <li><strong>1º lugar</strong> dos Best Team Leaders de Portugal — categoria Inspiring Leaders.</li>
                                <li><strong>1º lugar</strong> dos Best Team Leaders — Melhor Comunicação da Visão.</li>
                                <li>Eleito duas vezes <strong>Melhor Gestor de Pessoas</strong> em equipas de grande dimensão.</li>
                            </ul>
                        </div>

                        <div className="card-flat bg-[#f9f9fa]" data-testid="founder-quote-card">
                            <Quote size={20} className="text-[#b89b6f]" />
                            <blockquote className="font-display italic text-lg text-[#0d1628] mt-3 leading-snug">
                                &quot;Adelino Cunha is a professional who loves what he does. A dreamer and, more importantly, a do-er.&quot;
                            </blockquote>
                            <div className="label-caps mt-4">Dr. Richard Bandler · Co-criador da PNL</div>
                        </div>
                    </div>
                </div>

                {/* Clients strip */}
                <div className="border-t border-[#e4e4e7] bg-[#f9f9fa]">
                    <div className="max-w-7xl mx-auto px-6 md:px-10 py-12">
                        <div className="label-caps mb-6">Algumas das empresas que já confiaram na IHTP</div>
                        <div className="flex flex-wrap gap-2">
                            {CLIENTS.map((c) => (
                                <span key={c} className="text-sm bg-white border border-[#e4e4e7] text-[#0d1628] px-4 py-2 hover:border-[#0d1628] transition-colors">{c}</span>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* FLAGSHIP PRODUCTS */}
            <section className="border-t border-[#e4e4e7]" data-testid="flagship-products-section">
                <div className="max-w-7xl mx-auto px-6 md:px-10 py-24">
                    <div className="grid lg:grid-cols-12 gap-12 mb-12">
                        <div className="lg:col-span-5">
                            <div className="label-caps">Serviços IHTP</div>
                            <h2 className="font-display text-4xl md:text-5xl text-[#0d1628] mt-6 leading-tight">
                                Quer construir uma organização feliz? Comece aqui.
                            </h2>
                        </div>
                        <div className="lg:col-span-7 lg:pt-12">
                            <p className="text-[#52525b] text-lg leading-relaxed">
                                Dois serviços-âncora da IHTP, disponíveis na loja oficial em shop.ihavethepower.net.
                            </p>
                        </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-6">
                        {PRODUCTS.map((p, idx) => (
                            <article key={p.title} className="card-flat flex flex-col" data-testid={`product-card-${idx}`}>
                                <img src={p.image} alt={p.title} className="w-full h-64 object-cover -mx-8 -mt-8 mb-6" style={{ width: "calc(100% + 4rem)" }} />
                                <div className="label-caps">{p.tag}</div>
                                <h3 className="font-display text-4xl text-[#0d1628] mt-4 leading-tight">{p.title}</h3>
                                <div className="text-sm text-[#b89b6f] italic mt-2">{p.subtitle}</div>
                                <p className="text-[#52525b] mt-6 leading-relaxed flex-1">{p.description}</p>
                                <a href={p.url} target="_blank" rel="noreferrer" className="btn-primary mt-8 inline-flex self-start" data-testid={`product-cta-${idx}`}>
                                    {p.cta} <ArrowUpRight size={16} />
                                </a>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* INDICES SHOWCASE */}
            <section className="border-t border-[#e4e4e7] bg-[#fdf8f0]" data-testid="indices-section">
                <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-24">
                    <div className="max-w-3xl">
                        <div className="label-caps text-[#b89b6f]">Índices · Inquéritos · Benchmark</div>
                        <h2 className="font-display text-4xl md:text-5xl text-[#0d1628] mt-4 leading-tight">
                            Meça o que <em className="not-italic font-display text-[#b89b6f]">realmente</em> conta para o seu negócio.
                        </h2>
                        <p className="text-[#52525b] mt-5 leading-relaxed">
                            A plataforma inclui dois sistemas mundialmente reconhecidos — FIB e WHR — e permite criar índices personalizados adaptados à cultura e aos objetivos da sua empresa.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6 mt-12">
                        {/* FIB Card */}
                        <article className="border border-[#0d1628] bg-white p-7 flex flex-col" data-testid="index-card-fib">
                            <div className="flex items-center gap-2.5">
                                <Heart size={20} className="text-[#0d1628]" />
                                <div className="label-caps text-[#0d1628]">FIB</div>
                            </div>
                            <h3 className="font-display text-2xl text-[#0d1628] mt-4">Felicidade Interna Bruta</h3>
                            <p className="text-sm text-[#52525b] mt-3 flex-1 leading-relaxed">
                                Inspirado no índice de Felicidade Interna Bruta do Butão (1972), avalia 9 dimensões internas do bem-estar organizacional.
                            </p>
                            <ul className="mt-5 space-y-1.5 text-xs text-[#52525b]">
                                <li>• Bem-Estar Psicológico</li>
                                <li>• Saúde Física & Mental</li>
                                <li>• Equilíbrio Vida–Trabalho</li>
                                <li>• Aprendizagem & Crescimento</li>
                                <li>• Comunidade & Relações</li>
                                <li>• Diversidade & Inclusão</li>
                                <li className="text-[#a1a1aa]">+ 3 dimensões adicionais</li>
                            </ul>
                        </article>

                        {/* WHR Card */}
                        <article className="border border-[#b89b6f] bg-white p-7 flex flex-col" data-testid="index-card-whr">
                            <div className="flex items-center gap-2.5">
                                <Globe size={20} className="text-[#b89b6f]" />
                                <div className="label-caps text-[#b89b6f]">WHR</div>
                            </div>
                            <h3 className="font-display text-2xl text-[#0d1628] mt-4">World Happiness Report</h3>
                            <p className="text-sm text-[#52525b] mt-3 flex-1 leading-relaxed">
                                Baseado no World Happiness Report (ONU, Universidade de Oxford), mede os 6 fatores universais que distinguem as nações mais felizes do mundo.
                            </p>
                            <ul className="mt-5 space-y-1.5 text-xs text-[#52525b]">
                                <li>• Rendimento & Estabilidade</li>
                                <li>• Suporte Social</li>
                                <li>• Esperança de Vida Saudável</li>
                                <li>• Liberdade de Escolha</li>
                                <li>• Generosidade</li>
                                <li>• Confiança Institucional</li>
                            </ul>
                        </article>

                        {/* Custom Card */}
                        <article className="border-2 border-dashed border-[#8b5cf6] bg-white p-7 flex flex-col relative overflow-hidden" data-testid="index-card-custom">
                            <div className="flex items-center gap-2.5">
                                <Sparkles size={20} className="text-[#8b5cf6]" />
                                <div className="label-caps text-[#8b5cf6]">Personalizados</div>
                            </div>
                            <h3 className="font-display text-2xl text-[#0d1628] mt-4">Os seus próprios índices</h3>
                            <p className="text-sm text-[#52525b] mt-3 flex-1 leading-relaxed">
                                Crie índices à medida da sua organização para medir o que é realmente importante para a vossa cultura, estratégia e setor.
                            </p>
                            <ul className="mt-5 space-y-1.5 text-xs text-[#52525b]">
                                <li className="flex items-start gap-2">
                                    <Plus size={11} className="text-[#8b5cf6] mt-0.5 shrink-0" />
                                    <span>Ex.: <em className="not-italic">Engagement</em>, <em className="not-italic">Cultura de Reconhecimento</em></span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <Plus size={11} className="text-[#8b5cf6] mt-0.5 shrink-0" />
                                    <span>Ex.: <em className="not-italic">Alinhamento ESG</em>, <em className="not-italic">Inovação</em></span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <Plus size={11} className="text-[#8b5cf6] mt-0.5 shrink-0" />
                                    <span>Defina perguntas, escalas (1-5) e dimensões próprias</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <Plus size={11} className="text-[#8b5cf6] mt-0.5 shrink-0" />
                                    <span>Combine com FIB+WHR no mesmo radar</span>
                                </li>
                            </ul>
                        </article>
                    </div>

                    <div className="mt-10 flex flex-col md:flex-row gap-6 items-start border-t border-[#e4e4e7] pt-8">
                        <Brain size={32} className="text-[#b89b6f] shrink-0" />
                        <div className="flex-1">
                            <h4 className="font-display text-2xl text-[#0d1628]">Análise comparativa por IA com benchmark do ecossistema</h4>
                            <p className="text-[#52525b] mt-3 leading-relaxed max-w-3xl">
                                Compare automaticamente os scores da sua empresa contra a média do ecossistema IHTP e receba <strong className="text-[#0d1628]">recomendações estratégicas geradas por IA</strong> — diagnóstico, plano de ação por índice e KPIs medíveis. Tudo num radar chart sobreposto e snapshots históricos para medir progresso.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* PLATFORM VALUE PROP */}
            <section className="bg-[#0d1628] text-white" data-testid="platform-section">
                <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 grid lg:grid-cols-2 gap-16 items-center">
                    <div>
                        <div className="label-caps text-white/60">Plataforma digital</div>
                        <h2 className="font-display text-4xl md:text-5xl mt-6 leading-tight">
                            Gestão de projetos de felicidade, com o rigor de uma metodologia IHTP.
                        </h2>
                        <p className="text-white/70 mt-6 leading-relaxed max-w-xl">
                            Crie a sua conta gratuitamente, escolha um plano (1, 6 ou 12 meses) e comece a gerir projetos de transformação cultural com o acompanhamento estruturado da IHTP.
                        </p>
                        <div className="flex flex-wrap gap-4 mt-10">
                            <Link to="/register" className="btn-primary" style={{ background: "#fff", color: "#0d1628", borderColor: "#fff" }} data-testid="cta-register-bottom">
                                Registar a minha empresa
                            </Link>
                            <Link to="/login" className="btn-outline text-white border-white hover:bg-white/10" data-testid="cta-login-bottom">
                                Já tenho conta
                            </Link>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        {[
                            { icon: Globe, label: "Plataforma online 24/7" },
                            { icon: Users, label: "Gestão multi-utilizador" },
                            { icon: Award, label: "Metodologia IHTP" },
                            { icon: TrendingUp, label: "Indicadores reais" },
                        ].map(({ icon: Icon, label }) => (
                            <div key={label} className="border border-white/20 p-6">
                                <Icon size={20} className="text-white/80" />
                                <div className="font-display text-xl mt-4">{label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </MarketingLayout>
    );
}
